import type { UsefulInfoFieldId } from "./tripStore";

/** Information is scoped to places, not to a whole multi-country trip. */
export interface UsefulInfoDestination {
  id: string;
  name: string;
  query: string;
  date: string;
  latitude?: number;
  longitude?: number;
}

export interface UsefulInfoDestinationCandidate {
  name: string;
  date?: string;
  latitude?: number;
  longitude?: number;
  /** An address can disambiguate similarly named cities. */
  address?: string;
}

export type UsefulInfoEvidence = "approved" | "embedded" | "external" | "missing";

export interface UsefulInfoLocationFact {
  location: UsefulInfoDestination;
  countryCode: string;
  values: Partial<Record<UsefulInfoFieldId, string>>;
  descriptions: Partial<Record<UsefulInfoFieldId, string>>;
  evidence: Partial<Record<UsefulInfoFieldId, UsefulInfoEvidence>>;
  timeZone?: string;
}

export interface UsefulInfoFieldGroup {
  value: string;
  description: string;
  evidence: UsefulInfoEvidence;
  locations: UsefulInfoDestination[];
}

const fold = (value: string): string => String(value || "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase().trim().replace(/\s+/g, " ");

/** A trailing country/area in an address disambiguates homonymous cities. */
const addressScope = (address: string, city: string): string => {
  const parts = String(address || "").split(",").map(fold).filter(Boolean);
  if (parts.length < 2) return "";
  const last = parts[parts.length - 1];
  return last !== fold(city) && !/^\\d/.test(last) ? last : "";
};

/** Keep chronological first appearances; equal names with different coordinates are different places. */
export function uniqueUsefulInfoDestinations(
  tripDestination: string,
  candidates: readonly UsefulInfoDestinationCandidate[]
): UsefulInfoDestination[] {
  const nonEmpty = candidates.filter((candidate) => String(candidate.name || "").trim());
  const sorted = nonEmpty.map((candidate, index) => ({ ...candidate, index }))
    .sort((a, b) => (a.date || "9999-12-31").localeCompare(b.date || "9999-12-31") || a.index - b.index);
  const inputs: Array<UsefulInfoDestinationCandidate & { index: number }> = sorted.length ? sorted : [{ name: tripDestination, date: "", index: 0 }];
  const unique = new Map<string, UsefulInfoDestination>();
  for (const entry of inputs) {
    const name = String(entry.name || "").trim();
    if (!name) continue;
    const hasCoordinates = Number.isFinite(entry.latitude) && Number.isFinite(entry.longitude);
    const geoKey = hasCoordinates ? `${entry.latitude!.toFixed(3)}|${entry.longitude!.toFixed(3)}` : "";
    const normalizedName = fold(name);
    const scope = addressScope(entry.address || "", name);
    const key = hasCoordinates ? `geo:${geoKey}` : scope
      ? `name:${normalizedName}|area:${scope}` : `name:${normalizedName}`;
    const date = /^\d{4}-\d{2}-\d{2}$/.test(entry.date || "") ? String(entry.date) : "";
    const sameName = [...unique.values()].find((stored) => {
      if (fold(stored.name) !== normalizedName) return false;
      const otherScope = addressScope(stored.query, stored.name);
      if (scope && otherScope && scope !== otherScope) return false;
      if (hasCoordinates && stored.latitude !== undefined) return (
        Math.abs(stored.latitude - entry.latitude!) < 0.001 &&
        Math.abs((stored.longitude ?? 0) - entry.longitude!) < 0.001
      );
      return true;
    });
    if (!unique.has(key) && !sameName) {
      const locality: UsefulInfoDestination = {
        id: key, name, query: String(entry.address || "").trim() || name, date,
        ...(hasCoordinates ? { latitude: entry.latitude, longitude: entry.longitude } : {})
      };
      unique.set(key, locality);
    }
  }
  return [...unique.values()];
}

/** Missing data NEVER collapses into an apparently common value. */
export function groupUsefulInfoField(
  field: UsefulInfoFieldId,
  facts: readonly UsefulInfoLocationFact[]
): UsefulInfoFieldGroup[] {
  const groups = new Map<string, UsefulInfoFieldGroup>();
  for (const fact of facts) {
    const value = String(fact.values[field] || "").trim();
    const description = String(fact.descriptions[field] || "").trim();
    const evidence: UsefulInfoEvidence = value ? fact.evidence[field] || "embedded" : "missing";
    const normalizeValue = field === "plugType" && value
      ? value.split(",").map(fold).filter(Boolean).sort().join(",")
      : fold(value);
    const key = !value
      ? `missing:${fact.location.id}`
      : `${evidence}:${normalizeValue}|${fold(description)}`;
    const existing = groups.get(key);
    if (existing) existing.locations.push(fact.location);
    else groups.set(key, { value, description, evidence, locations: [fact.location] });
  }
  return [...groups.values()];
}

/** Prefer the itinerary date, never an assumed time zone derived from the country alone. */
export function isUsefulInfoCurrentDestination(location: UsefulInfoDestination, today: string): boolean {
  return Boolean(location.date && location.date === today);
}
